import { presenceLabel } from '../../../../lib/presence-labels';
import { relativeTime } from '../../../conversations/format';
import {
  bondLabelOf,
  formatDurationShort,
  SEQUENCE_FLAG_LABELS,
  SEVERITY_LABELS,
} from '../../labels';
import { isActivePriorityType } from '../../types';

import type { SupervisorScreenId } from '../../screens-ready';
import type { SupervisorDashboardState, PriorityItem } from '../../types';
import type {
  AttendantVM,
  KpiVM,
  BondVM,
  SequenceVM,
  PriorityQueueVM,
  SupervisionAlertVM,
  PresenceState,
  SequenceFlag,
  Severity,
} from '../../view-models';

const PLACEHOLDER = '—';
const PENDING_METRIC = 'métrica em implementação';

export interface Tela1ViewModel {
  kpis: KpiVM[];
  attendants: AttendantVM[];
  priorityQueue: PriorityQueueVM;
  supervisionAlerts: SupervisionAlertVM[];
  bonds: BondVM[];
  /** Contagem total de bonds com atividade (antes do slice top-4) — Item 317 Onda E. */
  bondsTotal: number;
  sequences: SequenceVM[];
  computedAt: string;
}

export type NameResolver = (userId: string) => string | undefined;

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/);
  const result = ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase();
  return result.length > 0 ? result : '?';
}

/** Junta partes não-vazias com " · " (ex "2 highVolume · 1 escalating"); undefined se vazio. */
function joinParts(parts: string[]): string | undefined {
  return parts.length > 0 ? parts.join(' · ') : undefined;
}

/** Formata espera (segundos) compacta e humanizada ("42s" · "35min" · "9h 13min"). null → "—". */
function fmtAwait(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return PLACEHOLDER;
  return formatDurationShort(seconds);
}

function adaptKpis(s: SupervisorDashboardState): KpiVM[] {
  const k = s.kpis;

  // ─── Subtextos descritivos computáveis do /state atual (Item 317 — FE-only) ───
  const awayCount = s.columnA.filter((e) => e.presenceStatus === 'away').length;
  const offlineCount = Math.max(0, k.attendantsTotal - k.attendantsOnline - awayCount);
  const slaBreachCount = s.columnB.filter((i) => i.type === 'sla_breach_2x').length;
  // A-06 (noturna 04/Set): a base é «abertas» (não terminais), a MESMA régua do aiConducting.
  // Dividir por conversationsActive (exclui operator-awaited) dava 22/2 = «1100% do total».
  // Sem o campo (backend antigo) não há percentual honesto — omite o descritor.
  const iaPct =
    k.conversationsOpen !== undefined && k.conversationsOpen > 0
      ? Math.round((k.aiConducting / k.conversationsOpen) * 100)
      : null;
  const seqDescriptor = joinParts([
    ...(s.sequencesRisk.filter((sq) => sq.flags.highVolumeFlag).length > 0
      ? [
          `${s.sequencesRisk.filter((sq) => sq.flags.highVolumeFlag).length} ${SEQUENCE_FLAG_LABELS.highVolume}`,
        ]
      : []),
    ...(s.sequencesRisk.filter((sq) => sq.flags.escalatingSentimentFlag).length > 0
      ? [
          `${s.sequencesRisk.filter((sq) => sq.flags.escalatingSentimentFlag).length} ${SEQUENCE_FLAG_LABELS.escalating}`,
        ]
      : []),
  ]);
  const confCritical = s.columnB.filter(
    (i) => i.type === 'conflict_alert' && i.severity === 'critical',
  ).length;
  const confHigh = s.columnB.filter(
    (i) => i.type === 'conflict_alert' && i.severity === 'high',
  ).length;
  const confDescriptor = joinParts([
    ...(confCritical > 0 ? [`${confCritical} ${SEVERITY_LABELS.critical}`] : []),
    ...(confHigh > 0 ? [`${confHigh} ${SEVERITY_LABELS.high}`] : []),
  ]);

  // ─── Item 317 Onda B — KPI 6 (aceite IA) + KPI 11 (awaiting médio) ───────
  const acceptanceRate = k.aiSuggestionAcceptanceRate;
  const aceiteHasValue = acceptanceRate !== null && acceptanceRate !== undefined;
  const awaiting = k.awaitingAverage;
  const awaitingHasValue =
    awaiting !== undefined &&
    (awaiting.customerSeconds !== null || awaiting.operatorSeconds !== null);

  // ─── KPI 8 — retomada por gatilho (v3.601; espelha o aceite-IA) ───────────
  const resumptionRate = k.triggerResumptionRate;
  const resumptionHasValue = resumptionRate !== null && resumptionRate !== undefined;

  return [
    {
      // A-52 (noturna 04/Set/2026): `conversationsActive` conta só new/assigned/in_progress/
      // awaiting_customer (Item 317) — exclui de propósito o que aguarda o time. Chamar isso de
      // «Conversas ativas» fazia a supervisora ler «2» com 317 abertas. O rótulo passa a dizer o
      // que a régua mede; o id e a série histórica `conversas-ativas` não mudam (sem degrau).
      id: 'conversas-ativas',
      label: 'Em atendimento',
      value: String(k.conversationsActive),
      cap: 'tela1.kpi.volume-basic',
    },
    {
      id: 'em-fila',
      label: 'Em fila',
      value: String(k.queueDepth),
      tone: k.queueDepth > 0 ? 'warning' : 'neutral',
      ...(slaBreachCount > 0 ? { descriptor: `${slaBreachCount} com SLA estourado` } : {}),
      cap: 'tela1.kpi.volume-basic',
    },
    {
      id: 'em-p1',
      label: 'Em P1',
      value: String(k.p1Active),
      tone: k.p1Active > 0 ? 'danger' : 'neutral',
      cap: 'tela1.kpi.volume-basic',
    },
    {
      id: 'atendentes',
      label: 'Atendentes',
      value: `${k.attendantsOnline}/${k.attendantsTotal}`,
      // N2-23: linguagem de operação (domicílio único `lib/presence-labels`), não o enum cru.
      descriptor: `${k.attendantsOnline} ${presenceLabel('online').toLowerCase()} · ${awayCount} ${presenceLabel('away').toLowerCase()} · ${offlineCount} ${presenceLabel('offline').toLowerCase()}`,
      cap: 'tela1.kpi.volume-basic',
    },
    {
      id: 'csat-medio',
      label: 'CSAT médio',
      value: k.csatAverage === null ? PLACEHOLDER : k.csatAverage.toFixed(1),
      ...(k.csatAverage === null ? {} : { sub: '/ 5.0' }),
      cap: 'tela1.kpi.volume-basic',
    },
    {
      id: 'aceite-ia',
      label: 'Aceite sugestão IA',
      value: aceiteHasValue ? `${String(Math.round(acceptanceRate * 100))}%` : PLACEHOLDER,
      ...(aceiteHasValue ? {} : { placeholderNote: PENDING_METRIC }),
      tone: 'ai',
      cap: 'tela1.kpi.aceite-ia',
    },
    {
      id: 'ia-dirigindo',
      label: 'IA dirigindo',
      value: String(k.aiConducting),
      tone: 'ai',
      ...(iaPct !== null ? { descriptor: `${iaPct}% das abertas` } : {}),
      cap: 'tela1.kpi.ia-dirigindo',
    },
    {
      id: 'retomada-gatilho',
      label: 'Retomada por gatilho',
      value: resumptionHasValue ? `${String(Math.round(resumptionRate * 100))}%` : PLACEHOLDER,
      ...(resumptionHasValue ? {} : { placeholderNote: PENDING_METRIC }),
      cap: 'tela1.kpi.retomada-gatilho',
    },
    {
      id: 'sequences-risco',
      label: 'Sequences sob risco',
      value: String(s.sequencesRisk.length),
      ...(seqDescriptor !== undefined ? { descriptor: seqDescriptor } : {}),
      cap: 'tela1.kpi.sequences',
    },
    {
      id: 'conflict-alerts',
      label: 'Conflict Alerts',
      value: String(k.conflictAlertsActive),
      tone: k.conflictAlertsActive > 0 ? 'danger' : 'neutral',
      ...(confDescriptor !== undefined ? { descriptor: confDescriptor } : {}),
      cap: 'tela1.kpi.conflict-alerts',
    },
    {
      id: 'awaiting-medio',
      label: 'Espera média',
      value: awaitingHasValue
        ? `${fmtAwait(awaiting.customerSeconds)} cliente · ${fmtAwait(awaiting.operatorSeconds)} operador`
        : PLACEHOLDER,
      // Valor composto (dois tempos) → fonte compacta, não o tamanho gigante de um número curto.
      ...(awaitingHasValue ? { valueCompact: true } : { placeholderNote: PENDING_METRIC }),
      cap: 'tela1.kpi.awaiting-times',
    },
  ];
}

/** Mescla sparklines + deltas do backend nos KpiVMs existentes (Item 317 Onda C). */
function applyTrends(kpis: KpiVM[], trends: SupervisorDashboardState['kpiTrends']): KpiVM[] {
  if (trends === undefined) return kpis;
  return kpis.map((k) => {
    const t = trends[k.id];
    if (t === undefined) return k;
    return {
      ...k,
      ...(t.sparkline.length >= 2 ? { spark: t.sparkline } : {}),
      ...(t.delta !== null ? { delta: t.delta > 0 ? `+${String(t.delta)}` : String(t.delta) } : {}),
      ...(t.delta !== null ? { deltaTrend: t.trend } : {}),
    };
  });
}

const PRESENCE_MAP: Record<string, PresenceState> = {
  online: 'online',
  away: 'away',
  offline: 'offline',
};

function formatLastAction(iso: string | null | undefined): string {
  if (iso === null || iso === undefined) return PLACEHOLDER;
  return relativeTime(iso);
}

function adaptAttendants(s: SupervisorDashboardState, resolveName: NameResolver): AttendantVM[] {
  return s.columnA.map((e) => {
    // Item 317 Onda A: backend enriquecido fornece modeDistribution → métricas reais.
    // Sem ele (backend antigo) → degrada honesto (carga X/Y).
    const enriched = e.modeDistribution !== undefined;
    const modes = e.modeDistribution ?? { h_a: 0, h_b: 0, h_c: 0 };
    const name = e.name ?? resolveName(e.userId) ?? PLACEHOLDER;
    // SLA per-atendente (Item 317): segundos do backend, humanizados aqui ("42s"/"9h 36min").
    // SlaStatus do VM não tem 'neutral' e o card sempre pinta a classe — quando sem dado,
    // mantemos placeholder no número (sem green falso de valor) e caímos no status default 'green'.
    return {
      id: e.userId,
      initials: initialsOf(name),
      name,
      role: e.role ?? PLACEHOLDER,
      flags: e.flags ?? [],
      ...(e.behavioralPattern != null ? { behavioralPattern: e.behavioralPattern } : {}),
      ...(e.coAttendance != null
        ? { coAttendance: { role: e.coAttendance.role, target: e.coAttendance.targetName } }
        : {}),
      ...(e.coAttendedBy != null ? { coAttendedBy: { from: e.coAttendedBy.fromName } } : {}),
      presence: PRESENCE_MAP[e.presenceStatus] ?? 'offline',
      lastAction: formatLastAction(e.lastActivityAt),
      specialties: [], // sem fonte no backend (Onda A não cobre)
      modes,
      slaStatus: e.slaStatus ?? 'green', // SlaStatus VM não tem 'neutral'; sem dado → default verde sem número
      sla: e.slaSeconds != null ? formatDurationShort(e.slaSeconds) : PLACEHOLDER,
      iaDirigindo: e.iaConducting ?? 0,
      loadSummary: { current: e.currentLoad, capacity: e.capacity },
      metricsAvailable: enriched,
    } satisfies AttendantVM;
  });
}

function adaptPriorityQueue(s: SupervisorDashboardState): PriorityQueueVM {
  const active = s.columnB.filter((i) => isActivePriorityType(i.type));
  const sev = (i: PriorityItem): Severity => (i.severity as Severity | undefined) ?? 'medium';
  return {
    // Item 317 Onda D: usa os campos humanos do backend quando presentes;
    // cai no placeholder/ref cru quando ausentes (backend antigo / dado faltante).
    conflictAlerts: active
      .filter((i) => i.type === 'conflict_alert')
      .map((i) => ({
        severity: sev(i),
        summary: i.summary ?? `Alerta ${i.ref}`,
        alertType: i.alertType ?? i.ref,
        detectedAt: i.detectedAt !== undefined ? formatLastAction(i.detectedAt) : PLACEHOLDER,
        ...(i.chainPosition !== undefined ? { chainPos: i.chainPosition } : {}),
      })),
    slaEstourado: active
      .filter((i) => i.type === 'sla_breach_2x')
      .map((i) => ({
        // Pessoa da conversa; breach de pendency cai no título (summary do Engine);
        // null (nunca o id cru do breach) → componente degrada honesto.
        name: i.personName ?? i.summary ?? null,
        waiting: fmtAwait(i.waitingSeconds),
        // conversationId vem tipado do Engine (só quando o recurso É conversa).
        ...(i.conversationId !== undefined ? { conversationId: i.conversationId } : {}),
      })),
    pendencyOverdue: active
      .filter((i) => i.type === 'pendency_4h_overdue')
      .map((i) => ({
        title: i.summary ?? 'Pendência sem título',
        operator: i.operatorName ?? PLACEHOLDER,
        overdue: fmtAwait(i.overdueSeconds),
        ...(i.conversationId !== undefined ? { conversationId: i.conversationId } : {}),
      })),
    // Fatia «promessa quebrada» — promessa do OPERADOR/IA ao CLIENTE, vencida.
    // Grupo PRÓPRIO e obrigatório: o adapter monta por filtro de tipo explícito,
    // então um `promise_broken` sem grupo passaria por `isActivePriorityType`
    // (que devolve true para tudo) e sumiria da tela — pior que antes da fatia.
    promisesBroken: active
      .filter((i) => i.type === 'promise_broken')
      .map((i) => ({
        promise: i.summary ?? 'Promessa sem descrição',
        person: i.personName ?? PLACEHOLDER,
        operator: i.operatorName ?? PLACEHOLDER,
        overdue: fmtAwait(i.overdueSeconds),
        ...(i.conversationId !== undefined ? { conversationId: i.conversationId } : {}),
      })),
    // Item 4 — compromissos quebrados do CLIENTE (CustomerCommitment broken). person/commitment
    // com fallback honesto (personName ausente → placeholder; nunca id cru).
    commitmentsBroken: active
      .filter((i) => i.type === 'commitment_broken')
      .map((i) => ({
        person: i.personName ?? PLACEHOLDER,
        commitment: i.summary ?? 'Compromisso sem descrição',
      })),
    // Item 5 — faltas (no_show). `when` = tempo relativo do detectedAt (scheduledAt do Engine);
    // `service` = nome do serviço (ou title fallback já resolvido no Engine em summary).
    noShows: active
      .filter((i) => i.type === 'no_show_24h')
      .map((i) => ({
        person: i.personName ?? PLACEHOLDER,
        when: i.detectedAt !== undefined ? formatLastAction(i.detectedAt) : PLACEHOLDER,
        service: i.summary ?? PLACEHOLDER,
      })),
  };
}

// Destino de navegação da linha da Coluna C. Tipado contra SupervisorScreenId
// (o onNavigate da página resolve `SCREEN_ROUTE[link]`) → um destino inválido/typo
// falha o tsc aqui, não vira `navigate(undefined)` em runtime. Alertas sem tela
// dedicada (sequences vivem na própria Painel de Equipe; atendentes-ausentes,
// checklist e contestações não têm superfície própria) ficam SEM link → a linha
// não navega e não se apresenta como botão clicável (honest-defer no componente).
const screen = (id: SupervisorScreenId): string => id;

function adaptSupervisionAlerts(s: SupervisorDashboardState): SupervisionAlertVM[] {
  const c = s.columnC;
  const alerts: SupervisionAlertVM[] = [
    {
      kind: 'queue',
      label: 'P1 ativos',
      count: c.p1ActiveCount,
      danger: c.p1ActiveCount > 0,
      link: screen('painel-p1'),
    },
    {
      kind: 'override',
      label: 'Overrides aguardando aprovação',
      count: c.overridesPendingCount,
      link: screen('aprovacao-overrides'),
    },
    {
      kind: 'conflict',
      label: 'ConflictAlerts ativos',
      count: c.conflictAlertsOpenCount,
      danger: c.conflictAlertsOpenCount > 0,
      link: screen('centro-alertas'),
    },
    { kind: 'sequence', label: 'Sequences sob risco', count: c.sequencesAtRiskCount },
    {
      kind: 'insights',
      label: 'Insights estratégicos pendentes',
      count: c.insightsPendingSupervisorCount,
      link: screen('insights'),
    },
  ];

  // ─── Item 317 Onda D — contadores adicionais (somente quando presentes) ────
  // Reutilizam kinds existentes do union (queue/attendant/behavioral/checklist).
  if (c.conversationsQueuedOver5minCount !== undefined) {
    alerts.push({
      kind: 'queue',
      label: 'Conversas em fila > 5min',
      count: c.conversationsQueuedOver5minCount,
      danger: c.conversationsQueuedOver5minCount > 0,
      link: screen('inbox-time'),
    });
  }
  if (c.attendantsAwayCount !== undefined) {
    alerts.push({
      kind: 'attendant',
      label: 'Atendentes ausentes',
      count: c.attendantsAwayCount,
    });
  }
  if (c.behavioralPatternsCriticalCount !== undefined) {
    alerts.push({
      kind: 'behavioral',
      label: 'Padrões comportamentais críticos',
      count: c.behavioralPatternsCriticalCount,
      danger: c.behavioralPatternsCriticalCount > 0,
      link: screen('dashboard-qualidade'),
    });
  }
  if (c.criticalChecklistPendingCount !== undefined) {
    alerts.push({
      kind: 'checklist',
      label: 'Checklist crítico pendente',
      count: c.criticalChecklistPendingCount,
      danger: c.criticalChecklistPendingCount > 0,
    });
  }
  if (c.analysisContestationsPendingCount !== undefined) {
    alerts.push({
      kind: 'behavioral',
      label: 'Contestações de análise pendentes',
      count: c.analysisContestationsPendingCount,
    });
  }

  return alerts;
}

function adaptBonds(s: SupervisorDashboardState): BondVM[] {
  return s.bondsActivity.slice(0, 4).map((b) => ({
    id: b.bondId,
    kind: b.kind ?? 'unknown',
    // label = BondType humanizado (alimenta o BondKindBadge — Tech Lead: TYPE ONLY,
    // sem número de referência). Titular fica na linha separada do card.
    label: b.kind !== undefined ? bondLabelOf(b.kind) : 'Bond',
    titular: b.titular ?? PLACEHOLDER,
    conversations: b.conversationsCount,
    sequences: b.sequencesCount ?? 0,
    // Item 355: surfaçar o conversationId da conversa mais recente do Bond p/ o "Ver Bond
    // detail". null no backend (sem conversa resolvível) → campo ausente no VM → honest-defer.
    ...(b.conversationId !== null ? { conversationId: b.conversationId } : {}),
    ...(b.adversarial !== undefined ? { adversarial: b.adversarial } : {}),
    ...(b.flags !== undefined ? { flags: b.flags as SequenceFlag[] } : {}),
    ...(b.criticalConflictAlerts !== undefined && b.criticalConflictAlerts > 0
      ? { conflictAlerts: { critical: b.criticalConflictAlerts } }
      : {}),
  }));
}

function flagsOf(f: SupervisorDashboardState['sequencesRisk'][number]['flags']): SequenceFlag[] {
  const out: SequenceFlag[] = [];
  if (f.highVolumeFlag) out.push('highVolume');
  if (f.escalatingSentimentFlag) out.push('escalating');
  if (f.allAbandonedFlag) out.push('allAbandoned');
  if (f.crossChannelInsistenceFlag) out.push('crossChannel');
  return out;
}

function adaptSequences(s: SupervisorDashboardState): SequenceVM[] {
  return s.sequencesRisk.map((seq) => ({
    id: seq.sequenceId,
    // Item 353: surfaçar o Person.id (roteável em /pessoas/:id) p/ o "Drill down".
    // null no backend (Person não resolvida) → campo ausente no VM → honest-defer.
    ...(seq.personId !== null ? { personId: seq.personId } : {}),
    // Item 354: surfaçar o conversationId da conversa atual (maior orderIndex) p/ o
    // "Ver conversa atual". null no backend (sem conversa resolvível) → ausente → honest-defer.
    ...(seq.conversationId !== null ? { conversationId: seq.conversationId } : {}),
    // Onda E: nomes/labels reais do backend; fallback honesto (placeholder/bondId cru).
    person: seq.personName ?? PLACEHOLDER,
    bond: seq.bondLabel ?? PLACEHOLDER,
    conversations: seq.totalConversations,
    sentiment: seq.sentiment ?? PLACEHOLDER,
    flags: flagsOf(seq.flags),
    attendant: {
      name: seq.attendant?.name ?? PLACEHOLDER,
      presence: seq.attendant?.presence ?? 'offline',
    },
  }));
}

export function adaptDashboardToTela1(
  s: SupervisorDashboardState,
  resolveName: NameResolver,
): Tela1ViewModel {
  return {
    kpis: applyTrends(adaptKpis(s), s.kpiTrends),
    attendants: adaptAttendants(s, resolveName),
    priorityQueue: adaptPriorityQueue(s),
    supervisionAlerts: adaptSupervisionAlerts(s),
    bonds: adaptBonds(s),
    bondsTotal: s.bondsActivity.length,
    sequences: adaptSequences(s),
    computedAt: s.computedAt,
  };
}
